import { ArrowLeft, CheckCircle, Eye, EyeOff, Lock, Save } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../../assets/scss/pages/SettingsPage.module.scss';
import { loginApi } from '../../../api/auth.api';
import api from '../../../config/axios-conf';
import { useAuthStore } from '../../../stores/authStore';

export default function ChangePassword() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const logout = useAuthStore((s) => s.logout);

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);

    const showToast = (type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 2500);
    };

    const handleSave = async () => {
        if (submitting) return;
        if (!oldPassword.trim()) { setError('Vui lòng nhập mật khẩu cũ'); return; }
        if (!newPassword.trim()) { setError('Vui lòng nhập mật khẩu mới'); return; }
        if (oldPassword === newPassword) { setError('Mật khẩu mới phải khác mật khẩu cũ'); return; }

        setSubmitting(true);
        setError('');
        try {
            // Verify old password
            try {
                await loginApi({ userName: user?.userName, passWord: oldPassword });
            } catch {
                setError('Mật khẩu cũ không đúng');
                setSubmitting(false);
                return;
            }

            // Update password
            await api.put(`/users/${user?.id}`, { passWord: newPassword });

            showToast('success', 'Đổi mật khẩu thành công. Vui lòng đăng nhập lại.');
            setTimeout(() => {
                logout();
                navigate('/admin/login');
            }, 1800);
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Không thể đổi mật khẩu');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className={styles.title}>Đổi mật khẩu</h1>
                    <div className={styles.subtitle}>Cập nhật mật khẩu đăng nhập của bạn</div>
                </div>
            </div>

            <div className={styles.printerLayout}>
                {/* LEFT: form */}
                <div className={styles.printerLeft}>
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionTitle}>Thông tin mật khẩu</div>
                        </div>
                        <div className={styles.form}>
                            {/* Old password */}
                            <div className={styles.formGroup}>
                                <div className={styles.formLabel}>Mật khẩu cũ</div>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className={styles.formInput}
                                        type={showOld ? 'text' : 'password'}
                                        value={oldPassword}
                                        onChange={(e) => { setOldPassword(e.target.value); setError(''); }}
                                        placeholder="Nhập mật khẩu cũ..."
                                        style={{ paddingRight: 44 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowOld((v) => !v)}
                                        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center' }}
                                    >
                                        {showOld ? <EyeOff size={17} /> : <Eye size={17} />}
                                    </button>
                                </div>
                            </div>

                            {/* New password */}
                            <div className={styles.formGroup}>
                                <div className={styles.formLabel}>Mật khẩu mới</div>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className={styles.formInput}
                                        type={showNew ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                                        placeholder="Nhập mật khẩu mới..."
                                        style={{ paddingRight: 44 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew((v) => !v)}
                                        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center' }}
                                    >
                                        {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, padding: '8px 12px', background: '#FFF5F5', borderRadius: 8, border: '1px solid #FECACA' }}>
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT: action panel */}
                <div className={styles.printerRight}>
                    <div className={styles.printerActionCard}>
                        <div className={styles.printerStatus}>
                            <Lock size={20} color="#0089F6" />
                            <div>
                                <div className={styles.printerStatusLabel}>
                                    <span style={{ fontWeight: 700 }}>Bảo mật tài khoản</span>
                                </div>
                                <div className={styles.printerStatusMeta}>{user?.userName}</div>
                            </div>
                        </div>

                        <div className={styles.printerDivider} />

                        <button
                            className={styles.btnSave}
                            style={{ width: '100%', justifyContent: 'center' }}
                            onClick={handleSave}
                            disabled={submitting}
                        >
                            <Save size={15} />
                            {submitting ? 'Đang xử lý...' : 'Đổi mật khẩu'}
                        </button>

                        <div className={styles.printerHint}>
                            Sau khi đổi, bạn sẽ được đăng xuất và cần đăng nhập lại.
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast */}
            {toast && (
                <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
                    {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                    {toast.text}
                </div>
            )}
        </div>
    );
}
